import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Button,
  Chip,
  Dialog,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textfield
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Skeleton, Stack, styled, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import { useSelectRowTable } from 'hooks/use-select-row-table'
import * as yup from 'yup'

import IconContainer from 'components/IconContainer/IconContainer'
import { renderItemCycles } from 'components/Modal/ModalChargeDetail/components/Composition/Composition'
import NumberFormatCustom from 'components/PorcentageInput/PorcentageInput'
import { Table } from 'components/Table'

import { formatMoney } from 'utils/FormatMoney'
import { toastFirstError } from 'utils/yupResolverWithToast'

import { theme } from 'styles/theme'

interface ApplyAdjustmentDialogProps {
  isOpen: boolean
  handleClose: () => void
  recurrenceUuid: string
  existingAdjustment?: {
    percentage_value?: number | string | null
    adjustment_type?: 'customized' | 'ipca' | 'igpm'
    frequency?: 'quarterly' | 'semester' | 'annual'
  } | null
  onSuccess?: () => void
}

interface ApplyAdjustmentForm {
  percentage_value: string
}

interface AdjustmentPreviewItem {
  uuid: string
  description: string | null
  product_uuid: string | null
  product_name: string | null
  quantity: number
  selected: boolean
  // Janela de ciclos já encerrada: o item não será mais cobrado e fica fora do
  // reajuste mesmo quando o merchant escolhe "todos os itens".
  expired: boolean
  start_cycle: number
  total_cycles: number
  current_amount_unit: number
  new_amount_unit: number
  current_amount_billed: number
  new_amount_billed: number
  amount_difference: number
}

interface AdjustmentPreview {
  recurrence_uuid: string
  current_amount: number
  adjustment_percentage: number
  adjustment_type: string
  new_amount: number
  amount_difference: number
  applies_to_all_items?: boolean
  adjusted_items_count?: number
  adjustment_scheduled_billing_item_uuids?: string[] | null
  billing_items?: AdjustmentPreviewItem[]
  frequency: string
  calculation_period: {
    start_date: string
    end_date: string
  }
}

interface RecurrenceBasicInfo {
  amount: number
}

const Separator = styled('div')(() => ({
  width: '1fr',
  flexGrow: '1',
  borderTop: '0.5px dashed #CBCBCB',
  alignSelf: 'center',
  marginTop: '2px'
}))

const applyAdjustmentSchema = yup.object().shape({
  percentage_value: yup
    .string()
    .required('Percentual é obrigatório')
    .test('valid-percentage', 'Percentual deve ser maior que 0', (value) => {
      const num = Number(value?.replace(',', '.'))
      return num > 0
    })
})

const ApplyAdjustmentDialog = ({
  isOpen,
  handleClose,
  recurrenceUuid,
  existingAdjustment,
  onSuccess
}: ApplyAdjustmentDialogProps) => {
  const [adjustmentPreview, setAdjustmentPreview] =
    useState<AdjustmentPreview | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [hasAutoFilledPercentage, setHasAutoFilledPercentage] = useState(false)
  const [noExistingAdjustment, setNoExistingAdjustment] = useState(false)
  const [recurrenceInfo, setRecurrenceInfo] =
    useState<RecurrenceBasicInfo | null>(null)
  const [hasInitializedScope, setHasInitializedScope] = useState(false)
  const { select, setSelect } = useSelectRowTable({ useSelected: true })

  const previewItems = adjustmentPreview?.billing_items ?? []
  const expiredUuids = previewItems
    .filter((item) => item.expired)
    .map((item) => item.uuid)

  // Seleção efetiva. O Table não permite desabilitar checkbox por linha e o
  // "selecionar todos" marca até os expirados, então a fonte da verdade é esta
  // lista saneada — nunca o estado bruto do componente.
  const effectiveSelection = select.selected.filter(
    (uuid) => !expiredUuids.includes(uuid)
  )

  // Itens que o merchant PODE marcar. Expirado nunca entra: não será mais cobrado,
  // e o backend o descarta mesmo quando a seleção não é informada.
  const selectableUuids = previewItems
    .filter((item) => !item.expired)
    .map((item) => item.uuid)

  const isItemAdjusted = (item: AdjustmentPreviewItem) =>
    !item.expired && effectiveSelection.includes(item.uuid)

  const adjustedItems = previewItems.filter(isItemAdjusted)

  // Nada desmarcado => null. "Tudo marcado" e "todos os itens" são indistinguíveis na
  // tela, mas guardar a lista congelaria o escopo do reajuste programado, deixando de
  // fora um item que venha a ser adicionado depois.
  const selectionForPayload =
    effectiveSelection.length === selectableUuids.length
      ? null
      : effectiveSelection

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<ApplyAdjustmentForm>({
      resolver: yupResolver(applyAdjustmentSchema),
      defaultValues: {
        percentage_value: ''
      }
    })

  const percentageValue = watch('percentage_value')

  // Fetch recurrence basic info when modal opens
  useEffect(() => {
    const fetchRecurrenceInfo = async () => {
      if (!isOpen || !recurrenceUuid) return

      try {
        const response = await DataService({
          type: 'GET',
          url: `/v3/recurrences/${recurrenceUuid}`
        })

        if (response?.data?.record) {
          setRecurrenceInfo({
            amount: response.data.record.amount
          })
        }
      } catch (error) {
        console.error('Error fetching recurrence info:', error)
      }
    }

    fetchRecurrenceInfo()
  }, [isOpen, recurrenceUuid])

  // Reset auto-fill flag when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setLoadingPreview(true)
      setAdjustmentPreview(null)
      setHasAutoFilledPercentage(false)
      setNoExistingAdjustment(false)
      setRecurrenceInfo(null)
      setHasInitializedScope(false)
      setSelect((prev) => ({ ...prev, selected: [] }))
      if (existingAdjustment?.percentage_value) {
        // NumericFormat with valueIsNumericString expects value with dot as decimal separator
        reset({
          percentage_value: existingAdjustment.percentage_value.toString()
        })
        setHasAutoFilledPercentage(true)
      } else {
        reset({
          percentage_value: ''
        })
      }
    }
  }, [isOpen, existingAdjustment, reset, setSelect])

  // Fetch adjustment preview for all types
  useEffect(() => {
    const fetchAdjustmentPreview = async () => {
      if (!isOpen || !recurrenceUuid) {
        setAdjustmentPreview(null)
        return
      }

      const percentageNumber = Number(percentageValue.replace(',', '.'))

      // If no existing adjustment and user hasn't entered a valid percentage yet
      // just show the input without loading
      if (noExistingAdjustment && percentageNumber <= 0) {
        setLoadingPreview(false)
        setAdjustmentPreview(null)
        return
      }

      setLoadingPreview(true)

      try {
        const url = `/v3/recurrences/${recurrenceUuid}/adjustment_preview?percentage_value=${percentageNumber}`

        const response = await DataService({
          type: 'GET',
          url
        })

        if (response?.data?.adjustment_preview) {
          const preview = response.data.adjustment_preview
          setAdjustmentPreview(preview)

          // Auto-fill the input with adjustment_percentage from API on first load
          // Only if there's an existing adjustment configured
          if (
            !hasAutoFilledPercentage &&
            !noExistingAdjustment &&
            preview.adjustment_percentage
          ) {
            setValue(
              'percentage_value',
              preview.adjustment_percentage.toString()
            )
            setHasAutoFilledPercentage(true)
          }

          // Hidrata o escopo com a seleção JÁ CONFIGURADA no reajuste programado.
          // Sem isto o modal abre em "todos os itens" mesmo com seleção salva — e,
          // como o payload manda billing_item_uuids: null nesse escopo, aplicar sem
          // tocar em nada APAGARIA a configuração.
          // Só na primeira carga: o preview é refeito a cada mudança de percentual e
          // não pode sobrescrever o que o merchant acabou de marcar.
          if (!hasInitializedScope) {
            const persistedSelection =
              preview.adjustment_scheduled_billing_item_uuids
            const selectableFromPreview = (preview.billing_items ?? [])
              .filter((item: AdjustmentPreviewItem) => !item.expired)
              .map((item: AdjustmentPreviewItem) => item.uuid)

            // Sem seleção salva, tudo marcado: o caso comum não exige interação
            // nenhuma. Item expirado fica de fora já na largada — não será cobrado,
            // e o backend o descarta de qualquer forma.
            setSelect((prev) => ({
              ...prev,
              selected: persistedSelection?.length
                ? persistedSelection
                : selectableFromPreview
            }))
            setHasInitializedScope(true)
          }
        } else if (response?.error) {
          // If no existing adjustment configured, allow manual input
          if (
            response?.message?.includes('não possui reajuste configurado') ||
            response?.message?.includes('Recorrência não possui reajuste')
          ) {
            setNoExistingAdjustment(true)
            setHasAutoFilledPercentage(true) // Show the input field
            setAdjustmentPreview(null)
          }
        }
      } catch (error) {
        console.error('Error fetching adjustment preview:', error)
        // Don't show toast for "no adjustment configured" error
      } finally {
        setLoadingPreview(false)
      }
    }

    // Debounce the preview fetch
    const timeoutId = setTimeout(() => {
      fetchAdjustmentPreview()
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [
    isOpen,
    recurrenceUuid,
    percentageValue,
    existingAdjustment,
    hasAutoFilledPercentage,
    noExistingAdjustment,
    hasInitializedScope,
    setSelect,
    setValue
  ])

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true)
  }

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false)
  }

  const handleApplyAdjustment = async () => {
    setLoading(true)
    const id = toast.loading('Aplicando reajuste...')

    const payload = {
      adjustment_scheduled_attributes: {
        percentage_value: Number(percentageValue.replace(',', '.')),
        billing_item_uuids: selectionForPayload
      }
    }

    const response = await DataService({
      type: 'PATCH',
      url: `/v3/recurrences/${recurrenceUuid}/apply_adjustment`,
      data: payload
    })

    if (response.error) {
      toast.update(id, {
        render: response.detail_error || response.message,
        type: 'error',
        isLoading: false,
        autoClose: 5000
      })
      setLoading(false)
      return
    }

    toast.update(id, {
      render: response.message,
      type: 'success',
      isLoading: false,
      autoClose: 5000
    })

    setLoading(false)
    setConfirmDialogOpen(false)
    handleClose()
    onSuccess?.()
  }

  const getAdjustmentTypeLabel = () => {
    if (noExistingAdjustment) return 'Personalizado'
    if (adjustmentPreview?.adjustment_type === 'ipca') return 'IPCA'
    if (adjustmentPreview?.adjustment_type === 'igpm') return 'IGPM'
    if (existingAdjustment?.adjustment_type === 'ipca') return 'IPCA'
    if (existingAdjustment?.adjustment_type === 'igpm') return 'IGPM'
    return 'Personalizado'
  }

  // Calculate local preview when no API preview is available
  const getLocalPreview = () => {
    if (!recurrenceInfo?.amount) return null

    const percentageNumber = Number(percentageValue.replace(',', '.'))
    if (percentageNumber <= 0) return null

    const currentAmount = recurrenceInfo.amount
    const newAmount = currentAmount * (1 + percentageNumber / 100)
    const amountDifference = newAmount - currentAmount

    return {
      current_amount: currentAmount,
      new_amount: newAmount,
      amount_difference: amountDifference
    }
  }

  // Novo total refletindo a seleção. Calculado no CLIENTE a partir do breakdown que o
  // preview já trouxe — marcar um checkbox não pode disparar request. É a mesma
  // aritmética do backend: soma dos itens, em valores brutos.
  const getSelectionAwareTotals = () => {
    if (!previewItems.length) return null

    const currentAmount = previewItems.reduce(
      (sum, item) => sum + item.current_amount_billed,
      0
    )
    const newAmount = previewItems.reduce(
      (sum, item) =>
        sum +
        (isItemAdjusted(item)
          ? item.new_amount_billed
          : item.current_amount_billed),
      0
    )

    return {
      current_amount: currentAmount,
      new_amount: newAmount,
      amount_difference: newAmount - currentAmount
    }
  }

  // Get current amount from preview or recurrence info
  const getCurrentAmount = () => {
    if (adjustmentPreview?.current_amount) {
      return formatMoney(adjustmentPreview.current_amount)
    }
    if (recurrenceInfo?.amount) {
      return formatMoney(recurrenceInfo.amount)
    }
    return '-'
  }

  // Get new amount from preview or local calculation
  const getNewAmount = () => {
    const totals = getSelectionAwareTotals()
    if (totals) return formatMoney(totals.new_amount)
    if (adjustmentPreview?.new_amount) {
      return formatMoney(adjustmentPreview.new_amount)
    }
    const localPreview = getLocalPreview()
    if (localPreview?.new_amount) {
      return formatMoney(localPreview.new_amount)
    }
    return '-'
  }

  // Get amount difference from preview or local calculation
  const getAmountDifference = () => {
    const totals = getSelectionAwareTotals()
    if (totals) return `+${formatMoney(totals.amount_difference)}`
    if (adjustmentPreview?.amount_difference) {
      return `+${formatMoney(adjustmentPreview.amount_difference)}`
    }
    const localPreview = getLocalPreview()
    if (localPreview?.amount_difference) {
      return `+${formatMoney(localPreview.amount_difference)}`
    }
    return '-'
  }

  // Sempre com contagem, inclusive em "todos os itens": item com janela expirada fica
  // fora mesmo nesse escopo, então dizer só "Todos os itens" esconderia a exclusão.
  const getAdjustedScopeLabel = () => {
    if (!previewItems.length) return '-'
    return `${adjustedItems.length} de ${previewItems.length} ${
      previewItems.length === 1 ? 'item' : 'itens'
    }`
  }

  // Bloqueia o avanço quando o escopo é "selecionar itens" mas nada elegível ficou
  // marcado — mesma leitura do backend, que rejeita lista vazia em vez de assumir "todos".
  const hasInvalidSelection =
    selectableUuids.length > 0 && effectiveSelection.length === 0

  const handleOpenConfirmDialogWithScope = () => {
    if (hasInvalidSelection) {
      toast.error('Selecione ao menos um item para o reajuste.')
      return
    }
    handleOpenConfirmDialog()
  }

  const hasPreviewData = () => {
    return adjustmentPreview || getLocalPreview()
  }

  return (
    <Modal
      handleClose={handleClose}
      open={isOpen}
      maxWidth="500px"
      width="100%"
    >
      <ModalBody>
        <ModalHeader onClose={handleClose}>
          <Stack direction="row" alignItems="center" gap={2}>
            <IconContainer icon="eventRepeat" />
            <Typography variant="mdBold">Aplicar reajuste</Typography>
          </Stack>
        </ModalHeader>

        <ModalContent>
          <Stack gap={2}>
            <Typography>
              O reajuste será aplicado apenas nas faturas programadas ainda não
              abertas para pagamento.
            </Typography>

            {/* Percentage Input Field - Always visible and editable for all types */}
            {!hasAutoFilledPercentage ? (
              <Box>
                <Typography
                  variant="smLight"
                  color={theme.palette.neutral[400]}
                  mb={0.5}
                >
                  Percentual do reajuste
                </Typography>
                <Skeleton variant="rounded" height={40} />
              </Box>
            ) : (
              <Controller
                control={control}
                name="percentage_value"
                render={({ field, fieldState: { error } }) => (
                  <Textfield
                    label="Percentual do reajuste"
                    placeholder="Ex: 10"
                    InputProps={{
                      inputComponent: NumberFormatCustom as any,
                      style: { fontSize: '14px' }
                    }}
                    value={field.value}
                    onChange={field.onChange}
                    helperText={error?.message}
                    error={!!error?.message}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '14px'
                      }
                    }}
                  />
                )}
              />
            )}

            {/* Itens do reajuste — todos marcados por padrão, o merchant desmarca o
                que não quer. Aparece mesmo com um item só, para o fluxo ser sempre o
                mesmo. A tabela também é o que mostra a janela de ciclos de cada item:
                sem ela, um item que só começa num ciclo futuro faz o total do modal
                divergir da próxima cobrança sem explicação. */}
            {previewItems.length > 0 && (
              <Box>
                <Typography
                  variant="smLight"
                  color={theme.palette.neutral[400]}
                  mb={0.5}
                >
                  Itens do reajuste
                </Typography>
                <Table
                  header={[
                    { id: 'item', label: 'Item' },
                    { id: 'currentAmount', label: 'Valor atual' },
                    { id: 'newAmount', label: 'Novo valor' }
                  ]}
                  rows={previewItems.map((item) => ({
                    uuid: item.uuid,
                    item: (
                      <Stack gap={0.5}>
                        <Typography variant="smBold">
                          {item.description ||
                            item.product_name ||
                            'Item sem descrição'}
                        </Typography>
                        <Stack direction="row" gap={0.5} alignItems="center">
                          <Typography
                            variant="smLight"
                            color={theme.palette.neutral[400]}
                          >
                            {renderItemCycles(item)}
                          </Typography>
                          {item.expired && (
                            <Chip
                              variant="gray"
                              label="Fora do reajuste"
                              size="small"
                            />
                          )}
                        </Stack>
                      </Stack>
                    ),
                    currentAmount: formatMoney(item.current_amount_billed),
                    newAmount: isItemAdjusted(item)
                      ? formatMoney(item.new_amount_billed)
                      : '-'
                  }))}
                  select={{
                    useSelect: true,
                    selected: select.selected,
                    setSelected: setSelect
                  }}
                  hoverInRow
                  hiddePagination
                  disableFilter
                />
                {hasInvalidSelection && (
                  <Typography
                    variant="xsLight"
                    sx={{
                      color: theme.palette.error?.main || '#D32F2F',
                      mt: '6px',
                      display: 'block'
                    }}
                  >
                    Selecione ao menos um item para o reajuste.
                  </Typography>
                )}
              </Box>
            )}

            {/* Preview Box - Always visible with skeleton during loading */}
            <Box
              sx={{
                padding: '12px',
                border: '1px solid #EDEBEB',
                borderRadius: '5px'
              }}
            >
              <Box
                display="flex"
                gap={1.25}
                alignItems="end"
                marginBottom="10px"
              >
                <Typography
                  variant="smLight"
                  color={theme.palette.neutral[400]}
                >
                  Tipo de reajuste
                </Typography>
                <Separator />
                {loadingPreview ? (
                  <Skeleton width={80} height={20} />
                ) : (
                  <Typography variant="smBold">
                    {getAdjustmentTypeLabel()}
                  </Typography>
                )}
              </Box>

              <Box
                display="flex"
                gap={1.25}
                alignItems="end"
                marginBottom="10px"
              >
                <Typography
                  variant="smLight"
                  color={theme.palette.neutral[400]}
                >
                  Valor atual
                </Typography>
                <Separator />
                {loadingPreview ? (
                  <Skeleton width={70} height={20} />
                ) : (
                  <Typography variant="smBold">{getCurrentAmount()}</Typography>
                )}
              </Box>

              <Box
                display="flex"
                gap={1.25}
                alignItems="end"
                marginBottom="10px"
              >
                <Typography
                  variant="smLight"
                  color={theme.palette.neutral[400]}
                >
                  Novo valor
                </Typography>
                <Separator />
                {loadingPreview ? (
                  <Skeleton width={70} height={20} />
                ) : (
                  <Typography variant="smBold">{getNewAmount()}</Typography>
                )}
              </Box>

              <Box
                display="flex"
                gap={1.25}
                alignItems="end"
                marginBottom={previewItems.length ? '10px' : undefined}
              >
                <Typography
                  variant="smLight"
                  color={theme.palette.neutral[400]}
                >
                  Diferença
                </Typography>
                <Separator />
                {loadingPreview ? (
                  <Skeleton width={60} height={20} />
                ) : (
                  <Typography
                    variant="smBold"
                    color={
                      hasPreviewData() ? theme.palette.success[300] : undefined
                    }
                  >
                    {getAmountDifference()}
                  </Typography>
                )}
              </Box>

              {!!previewItems.length && (
                <Box display="flex" gap={1.25} alignItems="end">
                  <Typography
                    variant="smLight"
                    color={theme.palette.neutral[400]}
                  >
                    Itens reajustados
                  </Typography>
                  <Separator />
                  {loadingPreview ? (
                    <Skeleton width={60} height={20} />
                  ) : (
                    <Typography variant="smBold">
                      {getAdjustedScopeLabel()}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Stack>
        </ModalContent>

        <ModalFooter>
          <Button variantButton="neutral" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            variantButton="primary"
            onClick={handleSubmit(
              handleOpenConfirmDialogWithScope,
              toastFirstError
            )}
            disabled={loadingPreview || hasInvalidSelection}
          >
            Aplicar
          </Button>
        </ModalFooter>
      </ModalBody>

      <Dialog
        icon="eventRepeat"
        title=""
        description={`${
          !previewItems.length || selectionForPayload === null
            ? 'O reajuste será aplicado em todos os itens'
            : `O reajuste será aplicado em ${adjustedItems.length} de ${previewItems.length} itens`
        } desta recorrência, apenas nas cobranças pendentes. Deseja continuar?`}
        setIsOpenDialog={handleCloseConfirmDialog}
        isOpenDialog={confirmDialogOpen}
      >
        <Button
          variantButton="neutral"
          onClick={handleCloseConfirmDialog}
          fullWidth
        >
          Voltar
        </Button>
        <Button
          autoFocus
          disabled={loading}
          loading={loading}
          onClick={handleApplyAdjustment}
          fullWidth
        >
          Confirmar
        </Button>
      </Dialog>
    </Modal>
  )
}

export default ApplyAdjustmentDialog
